import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert, Autocomplete, Box, Button, Card, CardContent, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  IconButton, List, ListItem, ListItemText, Stack, Switch, TextField, Typography,
} from "@mui/material";
import { DateTimePicker, PageHeader } from "common";
import I18n from "i18n";
import { useEffect, useState } from "react";
import AccessControlHistoryList from "./AccessControlHistoryList";
import {
  accessControlError, AccessControlResponse, AccessControlSettings, AccessControlEntry,
  AccessControlEntryType, buildAccessControlChanges,
  expiryFromInput, localExpiryInput, sameExpiry,
  loadAccessControl, saveAccessControl, validateAccessEntry,
} from "./accessControlApi";

type ListKey = AccessControlEntryType;
const lists: { key: ListKey; label: string }[] = [
  { key: "ip_whitelist", label: "ipWhite" },
  { key: "ip_blacklist", label: "ipBlack" },
  { key: "country_whitelist", label: "countries" },
];
const t = (key: string) => I18n.t(`AccessControl.${key}`);

export default function AccessControlPage() {
  const [status, setStatus] = useState<AccessControlResponse>();
  const [settings, setSettings] = useState<AccessControlSettings>();
  const [entries, setEntries] = useState<AccessControlEntry[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [adding, setAdding] = useState<ListKey>();
  const [value, setValue] = useState("");
  const [label, setLabel] = useState("");
  const [expiry, setExpiry] = useState("");
  const [editing, setEditing] = useState<AccessControlEntry>();
  const [now, setNow] = useState(Date.now());
  const [entryError, setEntryError] = useState("");
  const [removing, setRemoving] = useState<AccessControlEntry>();
  const [historyVersion, setHistoryVersion] = useState(0);
  const dirty = !!settings && (
    settings.enabled !== status?.settings.enabled
    || entries.some(entry => entry.id < 0)
    || entries.some(entry => entry.id > 0 && !sameExpiry(entry.expires_at,
      status?.entries.find(original => original.id === entry.id)?.expires_at))
    || !!status?.entries.some(entry => !entries.some(current => current.id === entry.id))
  );
  const canEditList = (key: ListKey) => key === "country_whitelist" || status?.can_manage === true;
  const countryLabel = (code: string) => {
    const country = status?.countries.find(item => item.code === code);
    return country ? `${country.name} (${code})` : code;
  };

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    let active = true;
    loadAccessControl().then(result => {
      if (!active) return;
      if (result.success && result.data) {
        setStatus(result.data);
        setSettings(result.data.settings);
        setEntries(result.data.entries);
      } else setError(accessControlError(result.error, t("loadError")));
    }).catch((reason: unknown) => {
      if (active) setError(reason instanceof Error ? reason.message : String(reason));
    });
    return () => { active = false; };
  }, []);

  async function addEntry() {
    const type = editing?.type ?? adding;
    if (!type || !settings || !canEditList(type)) return;
    const entry = value.trim();
    if (!entry) {
      setEntryError(t("required"));
      return;
    }
    if (!editing && entries.some(item => item.type === type && item.value === entry)) {
      setEntryError(t("duplicate"));
      return;
    }
    setBusy(true);
    setEntryError("");
    try {
      const expiresAt = type === "country_whitelist" ? expiryFromInput(expiry) : null;
      const result = await validateAccessEntry(
        type === "country_whitelist" ? "country" : "ip", entry,
        type === "country_whitelist" ? undefined : label.trim(), expiresAt,
      );
      if (!result.success) {
        setEntryError(accessControlError(result.error, t("invalidEntry")));
        return;
      }
      if (editing) {
        setEntries(entries.map(item => item.id === editing.id ? { ...item, expires_at: expiresAt } : item));
      } else setEntries([...entries, {
        id: Math.min(0, ...entries.map(item => item.id)) - 1,
        type,
        value: entry,
        label: type === "country_whitelist" ? null : label.trim() || null,
        ...(type === "country_whitelist" ? { expires_at: expiresAt } : {}),
      }]);
      setSaved(false);
      setAdding(undefined);
      setEditing(undefined);
    } catch (reason: unknown) {
      setEntryError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    if (!settings || !status) return;
    setBusy(true);
    setSaved(false);
    setError("");
    try {
      const result = await saveAccessControl(buildAccessControlChanges(status, settings.enabled, entries));
      if (result.success && result.data) {
        setStatus(result.data);
        setSettings(result.data.settings);
        setEntries(result.data.entries);
        setSaved(true);
        setHistoryVersion(version => version + 1);
      } else setError(accessControlError(result.error, t("saveError")));
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader title={t("title")} />
      <Stack spacing={2} sx={{ p: 2 }}>
        <Alert severity="warning">{t("warning")}</Alert>
        <Alert severity="info">{t("precedence")}</Alert>
        {error && <Alert severity="error" sx={{ whiteSpace: "pre-line" }}>{error}</Alert>}
        {saved && <Alert severity="success">{t("saved")}</Alert>}
        {!status && !error && <CircularProgress />}
        {status && settings && (
          <>
            {!status.can_manage && <Alert severity="info">{t("countriesOnly")}</Alert>}
            <Typography>{t("currentIp")}: {status.current_ip}</Typography>
            <Typography>{t("database")}: {status.database.available ? t("available") : t("missing")}</Typography>
            <Typography>{t("downloaded")}: {status.database.downloaded_at ?? "—"}</Typography>
            <Typography>{t("build")}: {status.database.build_at ?? "—"}</Typography>
            <Typography>{t("interval")}: {status.update_days}</Typography>
            <FormControlLabel
              label={t("enabled")}
              control={<Switch checked={settings.enabled} disabled={busy || !status.can_manage} onChange={event => {
                setSettings({ ...settings, enabled: event.target.checked });
                setSaved(false);
              }} />}
            />
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "repeat(3, minmax(0, 1fr))" }, gap: 2 }}>
              {lists.map(list => (
                <Card key={list.key} variant="outlined">
                  <CardContent>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="h6">{t(list.label)}</Typography>
                      <IconButton aria-label={`${t("add")}: ${t(list.label)}`} disabled={busy || !canEditList(list.key)}
                        onClick={() => { setAdding(list.key); setValue(""); setLabel(""); setExpiry(""); setEntryError(""); }}>
                        <AddIcon />
                      </IconButton>
                    </Stack>
                    <List dense aria-label={t(list.label)} sx={{ maxHeight: 320, overflow: "auto" }}>
                      {entries.filter(entry => entry.type === list.key).map(entry => (
                        <ListItem key={entry.id} secondaryAction={<>
                          {list.key === "country_whitelist" && <IconButton
                            aria-label={`${t("editExpiry")}: ${entry.value}`} disabled={busy}
                            onClick={() => {
                              setEditing(entry); setValue(entry.value); setExpiry(localExpiryInput(entry.expires_at)); setEntryError("");
                            }}><EditIcon /></IconButton>}
                          <IconButton edge="end" aria-label={`${t("delete")}: ${entry.value}`} disabled={busy || !canEditList(list.key)}
                            onClick={() => setRemoving(entry)}>
                            <DeleteIcon />
                          </IconButton>
                        </>}>
                          <ListItemText primary={list.key === "country_whitelist" ? countryLabel(entry.value) : entry.value}
                            secondary={list.key === "country_whitelist"
                              ? `${entry.expires_at && new Date(entry.expires_at).getTime() <= now ? t("expired") : t("active")} · ${
                                entry.expires_at ? `${t("expiresAt")}: ${new Date(entry.expires_at).toLocaleString(I18n.locale)}` : t("unlimited")}`
                              : entry.label}
                            sx={{ overflowWrap: "anywhere", pr: list.key === "country_whitelist" ? 6 : 1 }} />
                        </ListItem>
                      ))}
                    </List>
                    {!entries.some(entry => entry.type === list.key) && <Typography color="text.secondary">{t("empty")}</Typography>}
                  </CardContent>
                </Card>
              ))}
            </Box>
            {dirty && <Alert severity="info">{t("unsaved")}</Alert>}
            <Box><Button variant="contained" onClick={save} disabled={busy || !dirty}>
              {busy ? t("saving") : t("save")}
            </Button></Box>
            <AccessControlHistoryList key={historyVersion} />
          </>
        )}
      </Stack>
      <Dialog open={!!adding || !!editing} onClose={() => {
        if (!busy) { setAdding(undefined); setEditing(undefined); }
      }} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? `${t("editExpiry")}: ${countryLabel(editing.value)}`
          : `${t("add")}: ${t(lists.find(list => list.key === adding)?.label ?? "title")}`}</DialogTitle>
        <DialogContent>
          {editing ? null : adding === "country_whitelist" ? (
            <Autocomplete
              options={(status?.countries ?? []).filter(country => !entries.some(
                entry => entry.type === "country_whitelist" && entry.value === country.code,
              ))}
              getOptionLabel={country => `${country.name} (${country.code})`}
              value={status?.countries.find(country => country.code === value) ?? null}
              onChange={(_, country) => { setValue(country?.code ?? ""); setEntryError(""); }}
              disabled={busy}
              renderInput={params => <TextField {...params} label={t("country")} margin="dense"
                error={!!entryError} helperText={entryError || t("countryHelp")} />}
            />
          ) : (
            <Stack spacing={1}>
              <TextField fullWidth autoFocus margin="dense" label={t("ipAddress")} value={value} disabled={busy}
                onChange={event => { setValue(event.target.value); setEntryError(""); }}
                error={!!entryError} helperText={entryError || t("ipHelp")} />
              <TextField fullWidth label={t("ipLabel")} value={label} disabled={busy}
                inputProps={{ maxLength: 120 }} helperText={t("ipLabelHelp")}
                onChange={event => { setLabel(event.target.value); setEntryError(""); }} />
            </Stack>
          )}
          {(adding === "country_whitelist" || editing) && <>
            <DateTimePicker fullWidth margin="normal" id="country-expiry"
              label={t("expiresAt")} value={expiry || null} disabled={busy}
              is24Hours clearable pickerProps={{ disablePast: true }} helperText={t("expiryHelp")}
              onChange={event => {
                setExpiry((event.target.value ?? "").replace(" ", "T"));
                setEntryError("");
              }} />
            {entryError && <Alert severity="error">{entryError}</Alert>}
          </>}
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => { setAdding(undefined); setEditing(undefined); }}>{t("cancel")}</Button>
          <Button disabled={busy} onClick={addEntry}>{editing ? t("apply") : t("add")}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!removing} onClose={() => setRemoving(undefined)}>
        <DialogTitle>{t("confirmDelete")}</DialogTitle>
        <DialogContent>
          <Typography>{removing?.type === "country_whitelist" ? countryLabel(removing.value) : removing?.value}</Typography>
          {removing?.label && <Typography>{removing.label}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoving(undefined)}>{t("cancel")}</Button>
          <Button color="error" onClick={() => {
            if (removing && canEditList(removing.type)) {
              setEntries(entries.filter(entry => entry.id !== removing.id));
              setSaved(false);
            }
            setRemoving(undefined);
          }}>{t("delete")}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
